import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRing, X, Check } from "lucide-react";

const CATEGORY_LABEL = {
  listini: "Listini",
  sconti: "Condizioni di vendita",
  spedizioni: "Spedizioni",
  generico: "Comunicazione",
};

const dmy = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return ""; } };

/** Banner in-app: mostra ai Rivenditori Ufficiali gli avvisi non letti (aggiornamenti condizioni). */
export default function ResellerNotifications({ enabled = true }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  const load = () => {
    api.get("/reseller-notifications")
      .then(({ data }) => setItems((data.notifications || []).filter((n) => !n.read)))
      .catch(() => {});
  };

  useEffect(() => { if (enabled) load(); /* eslint-disable-next-line */ }, [enabled]);

  const markAllRead = async () => {
    try { await api.post("/reseller-notifications/mark-read", {}); } catch { /* noop */ }
    setItems([]);
  };

  const markOneRead = async (id) => {
    try { await api.post("/reseller-notifications/mark-read", { ids: [id] }); } catch { /* noop */ }
    setItems((prev) => prev.filter((n) => n.id !== id));
  };

  if (!enabled || dismissed || items.length === 0) return null;

  return (
    <div className="mb-4 rounded-xl border border-brema-red/40 bg-brema-red/5 p-4 dark:border-brand/40 dark:bg-brand/5"
      data-testid="reseller-notifications-banner">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-brema-red dark:text-brand">
          <BellRing className="h-4 w-4" />
          Novità dal tuo referente Brema Bike
          <span data-testid="reseller-notifications-count"
            className="rounded-full bg-brema-red/15 px-2 py-0.5 text-xs dark:bg-brand/15">{items.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="secondary" onClick={markAllRead} data-testid="notif-mark-all-read"
            className="h-7 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
            <Check className="mr-1 h-3 w-3" /> Segna tutti letti
          </Button>
          <button onClick={() => setDismissed(true)} data-testid="notif-dismiss"
            className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((n) => (
          <li key={n.id} data-testid="reseller-notification-item"
            className="flex items-start justify-between gap-3 rounded-lg border border-border bg-brema-card p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {CATEGORY_LABEL[n.category] || CATEGORY_LABEL.generico}
                </span>
                <span className="text-sm font-medium text-foreground">{n.title}</span>
              </div>
              {n.message && <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{n.message}</p>}
              <p className="mt-1 text-[11px] text-muted-foreground">{dmy(n.created_at)}</p>
            </div>
            <button onClick={() => markOneRead(n.id)} data-testid="notif-item-read"
              title="Segna come letto" className="shrink-0 text-muted-foreground hover:text-foreground">
              <Check className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
